import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import ConfirmPopup from './ConfirmPopup';
import JobPhotoViewerData from './JobPhotoViewer';

interface JobPhotosProps {
    jobId: number;
}

interface PhotoItem {
    insertedDateFormatted: string;
    photos: AppFile[];
}

interface AppFile {
    id: number;
    fileName: string;
    fileType: string;
    content: any;
    selected: boolean;
}

const JobPhotosData = (props: JobPhotosProps) => {
    const [loading, setLoading] = React.useState(true);
    const [photoList, setPhotoList] = React.useState<PhotoItem[]>([]);
    const [showConfirmModal, setShowConfirm] = React.useState(false);
    const [modalSaveDisabled, setModalSaveDisabled] = React.useState(false);
    const [showPhotoViewer, setShowPhotoViewer] = React.useState(false);
    const [photoIndex, setPhotoIndex] = React.useState({ dateIndex: 0, photoIndex: 0 });

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const token = await authService.getAccessToken();

        axios.get('api/Job/GetJobPhotos?JobId=' + props.jobId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            for (var i = 0; i < res.data.length; i++) {
                res.data[i].selected = false;
            }

            setPhotoList(res.data);
            setLoading(false);
        }).catch(error => {
            toast.error(error.message);
        });
    }

    const handleImportFile = (e: any) => {
        setLoading(true);
        importPhotos(e.target.files);
    }

    const importPhotos = async (files: File[]) => {
        //save photos
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        const data = new FormData()
        data.append('jobId', props.jobId.toString());
        data.append('subId', user.sub);

        for (var i = 0; i < files.length; i++) {
            data.append('photos', files[i]);
        }

        //save photos
        axios.post('api/Job/ImportJobPhoto', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            getData();
            toast.success("Photos have been uploaded");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //SELECT PHOTOS
    const selectPhoto = (e: any, dateIndex: number, photoIndex: number) => {
        e.preventDefault();

        var photos = [...photoList];
        var photosItems = photos[dateIndex].photos;
        var photoItem = photosItems[photoIndex];
        photoItem.selected = !photoItem.selected;

        setPhotoList(photos)
    }
    //END SELECT

    //DELETE
    const checkPhotosSelected = () => {
        let photosSelected = false;

        for (var i = 0; i < photoList.length; i++) {
            var photos = photoList[i].photos;
            for (var x = 0; x < photos.length; x++) {
                if (photos[x].selected) {
                    photosSelected = true;
                    break;
                }
            }
        }

        return photosSelected;
    }

    const deletePhoto = (e: any) => {
        e.preventDefault();
        if (checkPhotosSelected()) {
            setShowConfirm(true);
            setModalSaveDisabled(false);
        } else {
            toast.error("Select one or more photos to delete!");
        }
    }

    const hideConfirmModal = () => {
        setShowConfirm(false);
        setModalSaveDisabled(false);
    }

    const saveConfirmModal = () => {
        if (modalSaveDisabled) {
            return;
        }

        deleteJobPhoto();
    }

    const getPhotosSelected = () => {
        let photoIds = [];

        for (var i = 0; i < photoList.length; i++) {
            var photos = photoList[i].photos;
            for (var x = 0; x < photos.length; x++) {
                if (photos[x].selected) {
                    photoIds.push(photos[x].id);
                }
            }
        }

        return photoIds;
    }

    const deleteJobPhoto = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        var jobPhotos = [...photoList];

        var photoIds = getPhotosSelected();

        var data = {
            photoIds: photoIds,
            subId: user.sub
        };

        //delete photo
        axios.post('api/Job/DeleteJobPhotos', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                deleteSelectedPhotos(jobPhotos);
                setShowConfirm(false);
                setModalSaveDisabled(true);
                toast.success("Photo/s have been deleted");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });

    }

    const deleteSelectedPhotos = (jobPhotos: PhotoItem[]) => {
        for (var i = jobPhotos.length - 1; i >= 0; i--) {
            var photos = jobPhotos[i].photos;
            for (var x = photos.length - 1; x >= 0; x--) {
                if (photos[x].selected) {
                    photos.splice(x, 1);
                }
            }
        }

        setPhotoList(jobPhotos);
    } 
    //END DELETE

    //DOWNLOAD PHOTOS
    const downloadPhotos = (e: any) => {
        e.preventDefault();
        if (checkPhotosSelected()) {
            downloadSelectedPhotos();
        } else {
            toast.error("Select one or more photos to delete!");
        }
    }

    const downloadSelectedPhotos = async () => {
        const token = await authService.getAccessToken();

        var photoIds = getPhotosSelected();
        var fileName = "Job " + props.jobId + " Photos.zip";

        var data = {
            photoIds: photoIds,
            jobId: props.jobId
        };

        //delete photo
        axios.post('api/Job/DownloadJobPhotos', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` },
            responseType: 'blob'
        })
        .then(res => {
            //DOWNLOAD FILE TO BROWSER
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END DOWNLOAD PHOTOS

    //PHOTO VIEWER
    const openPhotoViewer = (e: any, dateIndex: number, photoIndex: number) => {
        e.preventDefault();
        setPhotoIndex({ dateIndex: dateIndex, photoIndex: photoIndex });
        setShowPhotoViewer(true);
    }

    const hidePhotoViewer = () => {
        setPhotoIndex({ dateIndex: 0, photoIndex:0 });
        setShowPhotoViewer(false);
    }

    //END PHOTO VIEWER

    const confirmPopup = <ConfirmPopup heading="Delete Photo/s?" text="Are you sure you want to delete selected photos?" hideConfirmModal={hideConfirmModal} showConfirmModal={showConfirmModal} saveConfirmModal={saveConfirmModal} />
    const photoViewer = <JobPhotoViewerData photoList={photoList} origDateIndex={photoIndex.dateIndex} origPhotoIndex={photoIndex.photoIndex} showModal={showPhotoViewer} hideModal={hidePhotoViewer} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmPopup}
                {photoViewer}
            </div>
            <div className={loading ? "hidden" : "makeitflexspacebetween"}>
                <input type="file" className="" name="file" accept="image/*" onChange={handleImportFile} multiple />
                <div>
                    <button className="defaultbutton defaultbutton__small marginRight10" onClick={downloadPhotos}>Download</button>
                    <button className="defaultbutton defaultbutton__small" onClick={deletePhoto}>Delete</button>
                </div>
            </div>
            <hr></hr>
            <div className={photoList.length === 0 ? "hidden" : "jobphotos--height overflowAuto"}>
                {photoList.map((photoItem, index) =>
                    <div key={index} className="marginBottom10">
                        <div><h1>{photoItem.insertedDateFormatted}</h1></div>
                        <div className="makeitflexwrap">
                        {photoItem.photos.map((photo, photoIndex) =>
                            <div key={photo.id} className="marginRight20 marginTop10 photoImport__deleteParent--position" >
                                <a className={"jobphotos--select" + (photo.selected ? " makeitdashgreen" : " makeitgray")} href="#" onClick={(e) => selectPhoto(e, index, photoIndex)}>
                                    <i className="fa-solid fa-circle-check mobileMenuSize alignIconCenter"></i>
                                </a>

                                <img className="jobPhotoSize" src={photo.id > 0 ? "data:" + photo.fileType + ";base64," + photo.content : photo.content} onClick={(e) => openPhotoViewer(e, index, photoIndex)}></img>
                            </div>
                        )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobPhotosData;